import type { AuthUser, LoginInput, LoginResponse } from '@najah/core'

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000'
const REFRESH_KEY = 'najah.refreshToken'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code: string = 'ERROR',
    /** أخطاء لكل حقل، تأتي من التحقّق بـ Zod على الخادم */
    public readonly fields?: Record<string, string>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/* ── الرموز ──
   رمز الوصول يبقى في الذاكرة فقط (لا يصله XSS عبر التخزين)،
   ورمز التحديث في localStorage ليبقى الدخول بعد إغلاق التطبيق. */
let accessToken: string | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

/** للتنزيلات التي تحتاج fetch خاماً خارج دالة api */
export function getAccessTokenForDownload(): string {
  return accessToken ?? ''
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY)
  } catch {
    return null
  }
}

export function setRefreshToken(token: string | null) {
  try {
    if (token) localStorage.setItem(REFRESH_KEY, token)
    else localStorage.removeItem(REFRESH_KEY)
  } catch {
    // التخزين معطّل — تبقى الجلسة صالحة حتى إغلاق التطبيق فقط
  }
}

/** يُستدعى عندما تنتهي الجلسة نهائياً، ليعيد التطبيق إلى شاشة الدخول */
let onSessionExpired: (() => void) | null = null
export function setSessionExpiredHandler(fn: (() => void) | null) {
  onSessionExpired = fn
}

async function parseError(res: Response): Promise<ApiError> {
  let body: { message?: string; code?: string; fields?: Record<string, string> } = {}
  try {
    body = await res.json()
  } catch {
    // الخادم لم يُرجع JSON (وسيط، أو انهيار) — نستعمل رسالة عامة
  }
  return new ApiError(res.status, body.message ?? 'حدث خطأ غير متوقع', body.code ?? 'ERROR', body.fields)
}

/* ── تحديث الرمز بطلب واحد مشترك ──
   عدة طلبات قد تُرجع 401 في نفس اللحظة؛ بدون هذا الحارس سترسل كلها
   طلب تحديث، وتدوير الرموز على الخادم يُبطل بعضها بعضاً. */
let refreshInFlight: Promise<string> | null = null

async function performRefresh(): Promise<string> {
  const token = getRefreshToken()
  if (!token) throw new ApiError(401, 'انتهت الجلسة، يرجى تسجيل الدخول من جديد', 'SESSION_EXPIRED')

  const res = await fetch(`${BASE_URL}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: token }),
  })

  if (!res.ok) {
    setRefreshToken(null)
    setAccessToken(null)
    onSessionExpired?.()
    throw await parseError(res)
  }

  const data = (await res.json()) as LoginResponse
  setAccessToken(data.accessToken)
  setRefreshToken(data.refreshToken)
  return data.accessToken
}

function refreshOnce(): Promise<string> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  signal?: AbortSignal
  /** لمسارات المصادقة العامة — لا ترفق رمزاً ولا تحاول التحديث */
  skipAuth?: boolean
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, skipAuth } = options

  const url = new URL(`${BASE_URL}/api${path}`)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
    }
  }

  const send = async (token: string | null): Promise<Response> =>
    fetch(url, {
      method,
      signal,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

  let res: Response
  try {
    res = await send(skipAuth ? null : accessToken)
  } catch {
    throw new ApiError(0, 'تعذّر الاتصال بالخادم — تحقّق من الشبكة', 'NETWORK_ERROR')
  }

  // رمز الوصول منتهٍ: نجدّده مرة واحدة ونعيد المحاولة
  if (res.status === 401 && !skipAuth && getRefreshToken()) {
    try {
      const fresh = await refreshOnce()
      res = await send(fresh)
    } catch (err) {
      throw err instanceof ApiError ? err : new ApiError(401, 'انتهت الجلسة', 'SESSION_EXPIRED')
    }
  }

  if (!res.ok) throw await parseError(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/* ── نقاط المصادقة ── */
export const authApi = {
  login: (input: LoginInput) => api<LoginResponse>('/auth/login', { method: 'POST', body: input, skipAuth: true }),
  me: () => api<AuthUser>('/auth/me'),
  logout: () => api<{ success: boolean }>('/auth/logout', { method: 'POST', body: { refreshToken: getRefreshToken() } }),
  refresh: refreshOnce,
}

export interface HealthResponse {
  status: 'ok' | 'degraded'
  database: 'up' | 'down'
  time: string
}
export const healthApi = {
  check: (signal?: AbortSignal) => api<HealthResponse>('/health', { skipAuth: true, signal }),
}
